import React, { createContext, useContext, useCallback } from 'react';
import { useMap } from 'react-leaflet';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUserPreferences } from './_UserPreferencesContext';
import { useTabState } from './_TabStateContext';

const NavigationContext = createContext();

export const NavigationProvider = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { setTabState, getTabState } = useTabState();
    const { siteFilter, siteOrder } = useUserPreferences();

    const GLOBAL_PARAMS = ['lang', 'bf', 'no-pins'];
    const LOCAL_PARAMS = ['page', 'tab', 'filter', 'sort'];

    const ROUTER_BASENAME = '/map';

    const HOME_PATH = '/home';
    const MENU_PATH = '/menu';

	const getLocationPath = () =>
	{
		const path = window.location.pathname.startsWith(ROUTER_BASENAME)
		? window.location.pathname.replace(ROUTER_BASENAME, '')
		: window.location.pathname;
		return path;
	}

	const sortURLParams = (params) =>
	{
		const orderedParams = new URLSearchParams();
		const allParams = [...GLOBAL_PARAMS, ...LOCAL_PARAMS];
		const paramKeys = Array.from(params.keys()).sort((a, b) =>
		{
			let aIndex = allParams.indexOf(a);
			let bIndex = allParams.indexOf(b);

			aIndex = aIndex === -1 ? Infinity : aIndex;
			bIndex = bIndex === -1 ? Infinity : bIndex;

			if (aIndex !== bIndex) return aIndex - bIndex;
			return a.localeCompare(b); // Alphabetical sort for non-specified params
		});

		paramKeys.forEach((key) =>
		{
			orderedParams.set(key, params.get(key));
		});

		return orderedParams;
	};

	const navigateWithParams = useCallback((path, additionalParams = {}, preserveOnlyGlobals = true, state = {}) =>
	{
		const currentParams = new URLSearchParams(window.location.search);
		const params = new URLSearchParams();

		if (preserveOnlyGlobals)
		{
			GLOBAL_PARAMS.forEach(param =>
			{
				if (currentParams.has(param))
				{
					params.set(param, currentParams.get(param));
				}
			});
		}
		else
		{
			currentParams.forEach((value, key) =>
			{
				if (additionalParams[key] === undefined)
				{
					params.set(key, value);
				}
			});
		}

		Object.entries(additionalParams).forEach(([key, value]) =>
		{
			params.set(key, value);
		});

		const sortedParams = sortURLParams(params);

		const options = {};

		if (state) options.state = state;

		navigate(`${path}?${sortedParams}`, options);
	},
	[navigate]);

	const navigateSetParam = useCallback((key, value, defaultValue = null) =>
	{
		const currentPath = getLocationPath();
		const currentParams = new URLSearchParams(window.location.search);
		
		// Don't update if the value hasn't changed
		if (currentParams.get(key) === value) {
			return;
		}
		
		if (value === null || value === defaultValue)
		{
			currentParams.delete(key);
		}
		else
		{
			currentParams.set(key, value);
		}
	
		// Special handling for tab parameters to sync with TabStateContext
		if (key === 'tab')
		{
			let componentId;
			
			if (currentPath.includes('/menu'))
			{
				componentId = 'menu-screen-exhibits';
			}
	
			if (componentId && value !== null)
			{
				const tabIndex = parseInt(value) - 1;
				setTabState(componentId, tabIndex);
			}
		}
	
		const sortedParams = sortURLParams(currentParams);
		navigate(`${currentPath}?${sortedParams}`);
	},
	[navigate, getLocationPath, setTabState]);

	const isHomePath = () =>
	{
		return location.pathname.includes(HOME_PATH);
	}

	const isMenuPath = () =>
	{
		return location.pathname.includes(MENU_PATH);
	}

	const isInfoPath = () =>
	{
		const path = getLocationPath();

		if (path.match(/\/building\/(\d+)/))
			return 'site';
		else if (path.match(/\/exterior\/(\d+)/))
			return 'exterior';
		else if (path.match(/\/restaurant\/(\d+)/))
			return 'food';
		else
			return null;
	}

	const openHomeModal = useCallback((params, state = {}) =>
	{
		navigateWithParams(HOME_PATH, params, true, state);
	},
	[navigateWithParams]);

	const openMenuModal = useCallback((params, state = {}) =>
	{
		const params2 = 
		{
			...params,
			filter: siteFilter,
			sort: siteOrder
		};

		// Get saved tab state
		const savedTabState = getTabState('menu-screen-exhibits');
		if (savedTabState !== undefined)
		{
			params2.tab = savedTabState + 1;
		}

		navigateWithParams(MENU_PATH, params2, true, state);
	},
	[navigateWithParams, siteFilter, siteOrder, getTabState]);

	const openInfoModal = useCallback((infoType, id) =>
	{
		const paddedId = String(id).padStart(2, '0');

		if (infoType === 'site')
		{
			navigateWithParams(`/building/${paddedId}`);
		}
		else if (infoType === 'exterior')
		{
			navigateWithParams(`/exterior/${paddedId}`);
		}
		else if (infoType === 'food')
		{
			navigateWithParams(`/restaurant/${paddedId}`);
		}
	},
	[navigateWithParams]);

	const closeNavigationModal = useCallback(() =>
	{
		navigateWithParams('/');
	},
	[navigateWithParams]);

	const isStampcardPath = () => {
		return location.pathname === '/stampcard';
	};
	
	const openStampcardModal = useCallback((params = {}, state = {}) =>
	{
		navigateWithParams('/stampcard', params, true, {
			...state,
			state: {
				...state,
				from: state.from || location.pathname
			}
		});
	}, [navigateWithParams]);

	// Global function for direct home navigation from modals
	const navigateToHomeDirect = useCallback(() =>
	{
		// First close any open modals by forcing a URL change
		// This ensures consistent behavior regardless of which modal is open
		closeNavigationModal();
		
		// Then navigate to home with a slight delay to ensure the modal closing completes
		setTimeout(() => {
			openHomeModal();
		}, 10);
	},
	[closeNavigationModal, openHomeModal]);

	return (
		<NavigationContext.Provider value={{
			navigateWithParams,
			navigateSetParam,
			isHomePath,
			openHomeModal,
			isMenuPath,
			openMenuModal,
			isInfoPath,
			openInfoModal,
			closeNavigationModal,
			isStampcardPath,
    		openStampcardModal,
			navigateToHomeDirect
		}}>
			{children}
		</NavigationContext.Provider>
	);
};

export const useNavigation = () => useContext(NavigationContext);
